import React, { useEffect, useState, useRef, Fragment } from 'react';
import { PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, CodeIcon, TrendingUpIcon, TrendingDownIcon, Trash2Icon, FileTextIcon, PlusCircleIcon, Edit3Icon, PackageIcon, CheckCircle2Icon, AlertCircleIcon, LinkIcon, ChevronRightIcon, ChevronsDownIcon, ChevronsUpIcon, XIcon, LayersIcon, HistoryIcon, SaveIcon, UnlinkIcon } from 'lucide-react';
import { InvoiceDocument, LineItem, PurchaseOrderMatch, GoodsReceiptMatch } from '../types/invoice';
import { SelectedGLDimensions } from '../types/glCoding';
import { LineItem as GLLineItem } from '../financial/gl-coding/types';
import { Button } from './ui/Button';
import { GLCodingDialog } from '../../GLCodingDialog';
import { POMatchingModal } from './POMatchingModal';
import { TaxCodeSelector } from './TaxCodeSelector';
import { BulkGLCodingModal } from './BulkGLCodingModal';
import { PreviousCodingModal } from './PreviousCodingModal';
import { ValidationResult } from '../utils/invoiceValidation';
import { handleTaxCodeChange } from '../utils/taxCalculations';
import { formatCurrency, formatAmount } from '../utils/currencyFormat';
interface LineItemsPanelProps {
  document: InvoiceDocument;
  onUpdateLineItem?: (index: number, updatedItem: LineItem) => void;
  validation?: ValidationResult;
  isEditAllMode?: boolean;
}
export function LineItemsPanel({
  document,
  onUpdateLineItem,
  validation,
  isEditAllMode = false
}: LineItemsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState<number | null>(null);
  const [poMatchingLineIndex, setPOMatchingLineIndex] = useState<number | null>(null);
  const [expandedLineItems, setExpandedLineItems] = useState<Set<number>>(new Set());
  const [showBulkGLModal, setShowBulkGLModal] = useState(false);
  const [showPreviousCodingModal, setShowPreviousCodingModal] = useState(false);
  useEffect(() => {
    if (document.matchingType === '2-way' || document.matchingType === '3-way') {
      const matchedIndices = document.lineItems.map((item, index) => item.poMatch || item.grMatch ? index : -1).filter(index => index !== -1);
      setExpandedLineItems(new Set(matchedIndices));
    }
  }, [document.matchingType, document.lineItems.length]);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);
  const [editedLineItem, setEditedLineItem] = useState<LineItem | null>(null);
  const [newLineItem, setNewLineItem] = useState<LineItem | null>(null);
  const [isAddingNewLine, setIsAddingNewLine] = useState(false);
  const [draftLineItems, setDraftLineItems] = useState<LineItem[]>([]);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [deletingLineIndex, setDeletingLineIndex] = useState<number | null>(null);
  useEffect(() => {
    if (isEditAllMode) {
      setIsEditMode(true);
      setDraftLineItems([...document.lineItems]);
    } else {
      setIsEditMode(false);
      setEditingLineIndex(null);
      setEditedLineItem(null);
      setIsAddingNewLine(false);
      setNewLineItem(null);
      setDraftLineItems([]);
    }
  }, [isEditAllMode]);
  useEffect(() => {
    if (!isEditMode) {
      setDraftLineItems([...document.lineItems]);
    }
  }, [document.lineItems, isEditMode]);
  useEffect(() => {
    if (isEditMode && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 0);
    }
  }, [isEditMode]);
  const toggleLineItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedLineItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLineItems(newExpanded);
  };
  const expandAllLineItems = () => {
    const allMatchedIndices = document.lineItems.map((item, index) => item.poMatch || item.grMatch ? index : -1).filter(index => index !== -1);
    setExpandedLineItems(new Set(allMatchedIndices));
  };
  const collapseAllLineItems = () => {
    setExpandedLineItems(new Set());
  };
  const handleEditModeToggle = () => {
    if (!isEditAllMode) {
      const newEditMode = !isEditMode;
      setIsEditMode(newEditMode);
      if (newEditMode) {
        setDraftLineItems([...document.lineItems]);
        setEditingLineIndex(null);
        setEditedLineItem(null);
      } else {
        setEditingLineIndex(null);
        setEditedLineItem(null);
        setIsAddingNewLine(false);
        setNewLineItem(null);
        setDraftLineItems([]);
      }
    }
  };
  const handleSaveAllChanges = () => {
    if (!onUpdateLineItem) return;
    draftLineItems.forEach((item, index) => {
      onUpdateLineItem(index, item);
    });
    setIsEditMode(false);
    setDraftLineItems([]);
  };
  const handleCancelAllChanges = () => {
    setIsEditMode(false);
    setDraftLineItems([]);
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const updateDraftLineItem = (index: number, updatedItem: LineItem) => {
    setDraftLineItems(prev => {
      const updated = [...prev];
      updated[index] = updatedItem;
      return updated;
    });
  };
  const handleAddNewLine = () => {
    const newLine: LineItem = {
      id: `line-new-${Date.now()}`,
      itemCode: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      subtotal: 0,
      taxCode: 'I',
      taxAmt: 0,
      total: 0,
      type: 'CR',
      glCode: ''
    };
    setNewLineItem(newLine);
    setIsAddingNewLine(true);
  };
  const handleSaveNewLine = () => {
    if (!newLineItem || !onUpdateLineItem) return;
    const newIndex = document.lineItems.length;
    onUpdateLineItem(newIndex, newLineItem);
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const handleCancelNewLine = () => {
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const updateNewLineField = (field: keyof LineItem, value: any) => {
    if (!newLineItem) return;
    const updated = {
      ...newLineItem,
      [field]: value
    };
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      const subtotal = qty * price;
      let taxRate = 0.1;
      if (updated.taxCode === 'E') taxRate = 0;
      if (updated.taxCode === 'Z') taxRate = 0;
      const taxAmt = subtotal * taxRate;
      const total = subtotal + taxAmt;
      updated.quantity = qty;
      updated.unitPrice = price;
      updated.subtotal = subtotal;
      updated.taxAmt = taxAmt;
      updated.total = total;
    }
    setNewLineItem(updated);
  };
  const handleEditLineClick = (index: number) => {
    setEditingLineIndex(index);
    setEditedLineItem({
      ...document.lineItems[index]
    });
  };
  const handleSaveLineEdit = () => {
    if (editingLineIndex !== null && editedLineItem && onUpdateLineItem) {
      onUpdateLineItem(editingLineIndex, editedLineItem);
      setEditingLineIndex(null);
      setEditedLineItem(null);
    }
  };
  const handleCancelLineEdit = () => {
    setEditingLineIndex(null);
    setEditedLineItem(null);
  };
  const handleDeleteLine = (index: number) => {
    if (isEditMode && draftLineItems.length > 0) {
      const updatedDraft = draftLineItems.filter((_, i) => i !== index);
      setDraftLineItems(updatedDraft);
    } else if (onUpdateLineItem) {
      console.log('Delete line:', index);
    }
    setDeletingLineIndex(null);
  };
  const handleCancelDelete = () => {
    setDeletingLineIndex(null);
  };
  const updateEditedField = (field: keyof LineItem, value: any) => {
    if (!editedLineItem) return;
    const updated = {
      ...editedLineItem,
      [field]: value
    };
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      const subtotal = qty * price;
      let taxRate = 0.1;
      if (updated.taxCode === 'E') taxRate = 0;
      if (updated.taxCode === 'Z') taxRate = 0;
      const taxAmt = subtotal * taxRate;
      const total = subtotal + taxAmt;
      updated.quantity = qty;
      updated.unitPrice = price;
      updated.subtotal = subtotal;
      updated.taxAmt = taxAmt;
      updated.total = total;
    }
    setEditedLineItem(updated);
  };
  const handleGLCodeClick = (index: number) => {
    if (!isEditMode) return;
    setSelectedLineItemIndex(index);
  };
  const handleGLCodeClose = () => {
    setSelectedLineItemIndex(null);
  };
  const handleBulkGLApply = (glCode: string, dimensions: SelectedGLDimensions) => {
    if (!onUpdateLineItem) return;
    for (let i = document.lineItems.length - 1; i >= 0; i--) {
      const updatedItem = {
        ...document.lineItems[i],
        glCode
      };
      onUpdateLineItem(i, updatedItem);
    }
    setShowBulkGLModal(false);
  };
  const handlePreviousCodingApply = (glCode: string) => {
    if (!onUpdateLineItem) return;
    for (let i = document.lineItems.length - 1; i >= 0; i--) {
      const updatedItem = {
        ...document.lineItems[i],
        glCode
      };
      onUpdateLineItem(i, updatedItem);
    }
    setShowPreviousCodingModal(false);
  };
  const handlePOMatchClick = (index: number) => {
    if (!isEditMode) return;
    setPOMatchingLineIndex(index);
  };
  const handlePOMatchApply = (poMatch: PurchaseOrderMatch, grMatch?: GoodsReceiptMatch) => {
    if (poMatchingLineIndex !== null && onUpdateLineItem) {
      const currentItem = document.lineItems[poMatchingLineIndex];
      const updatedItem: LineItem = {
        ...currentItem,
        poMatch,
        grMatch,
        quantity: poMatch.matchedQuantity,
        unitPrice: poMatch.poUnitPrice,
        subtotal: poMatch.matchedAmount / 1.1,
        taxAmt: poMatch.matchedAmount - poMatch.matchedAmount / 1.1,
        total: poMatch.matchedAmount
      };
      onUpdateLineItem(poMatchingLineIndex, updatedItem);
    }
    setPOMatchingLineIndex(null);
  };
  const handlePOMatchClose = () => {
    setPOMatchingLineIndex(null);
  };
  const handleTaxCodeChangeForItem = (index: number, newTaxCode: string) => {
    if (!isEditMode) return;
    const currentItem = isEditMode && draftLineItems.length > 0 ? draftLineItems[index] : document.lineItems[index];
    const recalculated = handleTaxCodeChange(currentItem.subtotal, currentItem.taxAmt, currentItem.total, newTaxCode, currentItem.taxCode);
    const updatedItem = {
      ...currentItem,
      taxCode: newTaxCode,
      subtotal: recalculated.subtotal,
      taxAmt: recalculated.taxAmt,
      unitPrice: recalculated.subtotal / currentItem.quantity
    };
    updateDraftLineItem(index, updatedItem);
  };
  // Vendor ID mapping for GL Coding Dialog vendor defaults
  const getVendorInfo = (): {
    id: string;
    name: string;
  } | undefined => {
    const supplier = document.supplier;
    if (!supplier) return undefined;
    const vendorMap: Record<string, string> = {
      'Acme Corp': 'acme-consulting',
      'Office Plus': 'officeworks',
      'Tech Solutions': 'dell',
      'Global Supplies': 'amazon-business'
    };
    return {
      id: vendorMap[supplier] || supplier.toLowerCase().replace(/\s+/g, '-'),
      name: supplier
    };
  };
  // Adapt invoice LineItem to GL Coding LineItem format
  const getGLLineItem = (index: number): GLLineItem => {
    const item = index === -1 ? newLineItem : document.lineItems[index];
    return {
      id: index,
      description: item?.description || '',
      glCode: item?.glCode || '',
      selectedDimensions: undefined,
      vendor: getVendorInfo(),
      dimensionCount: 5
    };
  };
  const taxCodeErrors = validation?.errors.filter(e => e.field === 'taxCode' || e.field === 'taxAmt') || [];
  const hasTaxCodeError = taxCodeErrors.length > 0;
  const taxCodeErrorMessage = taxCodeErrors.map(e => e.message).join('. ');
  const matchingType = document.matchingType;
  const isNonPO = matchingType === 'non-po';
  const hasAnyMatchedLines = document.lineItems.some(item => item.poMatch || item.grMatch);
  const allMatchedLinesExpanded = hasAnyMatchedLines && document.lineItems.every((item, index) => !(item.poMatch || item.grMatch) || expandedLineItems.has(index));
  const displayLineItems = isAddingNewLine && newLineItem ? [...(isEditMode && draftLineItems.length > 0 ? draftLineItems : document.lineItems), newLineItem] : isEditMode && draftLineItems.length > 0 ? draftLineItems : document.lineItems;
  return <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-6226">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-6227">
          <div className="flex items-center gap-3" data-id="element-6228">
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-1 hover:bg-gray-200 rounded transition-colors" aria-label={isExpanded ? 'Collapse line items' : 'Expand line items'} data-id="element-6229">
              {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-600" data-id="element-6230" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" data-id="element-6231" />}
            </button>
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-6232">
              Lines ({displayLineItems.length})
            </h3>
            {document.matchingType && <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${document.matchingType === '3-way' ? 'bg-green-100 text-green-700' : document.matchingType === '2-way' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-6233">
                {document.matchingType === '3-way' && '3-Way Match'}
                {document.matchingType === '2-way' && '2-Way Match'}
                {document.matchingType === 'non-po' && 'Non-PO'}
              </span>}
            {isEditMode && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700" data-id="element-6234">
                Editing
              </span>}
            <div className="h-6 w-px bg-gray-300 mx-1" data-id="element-6235"></div>
            <div className="flex items-center gap-6 text-sm" data-id="element-6236">
              <div className="flex items-baseline gap-1.5" data-id="element-6237">
                <span className="text-gray-600" data-id="element-6238">Subtotal:</span>
                <span className="font-semibold text-gray-900" data-id="element-6239">
                  {formatCurrency(document.subtotal, document.currency, true)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5" data-id="element-6240">
                <span className="text-gray-600" data-id="element-6241">Tax:</span>
                <span className="font-semibold text-gray-900" data-id="element-6242">
                  {formatCurrency(document.taxAmt, document.currency, true)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5" data-id="element-6243">
                <span className="text-gray-600" data-id="element-6244">Total:</span>
                <span className="font-semibold text-gray-900" data-id="element-6245">
                  {formatCurrency(document.total, document.currency, true)}
                </span>
              </div>
            </div>
          </div>

          {isExpanded && <div className="flex gap-2" data-id="element-6246">
              {isEditMode && isNonPO && <>
                  <Button variant="ghost" size="sm" onClick={() => setShowPreviousCodingModal(true)} data-id="element-6247">
                    <HistoryIcon className="w-4 h-4 mr-1.5" data-id="element-6248" />
                    Previous Coding
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowBulkGLModal(true)} data-id="element-6249">
                    <LayersIcon className="w-4 h-4 mr-1.5" data-id="element-6250" />
                    Apply GL to All
                  </Button>
                  <div className="w-px h-6 bg-gray-300" data-id="element-6251"></div>
                </>}

              {(matchingType === '2-way' || matchingType === '3-way') && hasAnyMatchedLines && <>
                    {allMatchedLinesExpanded ? <Button variant="ghost" size="sm" onClick={collapseAllLineItems} data-id="element-6252">
                        <ChevronsUpIcon className="w-4 h-4 mr-1.5" data-id="element-6253" />
                        Collapse All
                      </Button> : <Button variant="ghost" size="sm" onClick={expandAllLineItems} data-id="element-6254">
                        <ChevronsDownIcon className="w-4 h-4 mr-1.5" data-id="element-6255" />
                        Expand All
                      </Button>}
                    <div className="w-px h-6 bg-gray-300" data-id="element-6256"></div>
                  </>}

              {isEditMode && <>
                  <Button variant="ghost" size="sm" data-id="element-6257">
                    <Trash2Icon className="w-4 h-4 mr-1.5" data-id="element-6258" />
                    Delete All
                  </Button>
                  {isNonPO && <Button variant="ghost" size="sm" data-id="element-6259">
                      <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-6260" />
                      Create Line From Header
                    </Button>}
                  <Button variant="ghost" size="sm" onClick={handleAddNewLine} disabled={isAddingNewLine} data-id="element-6261">
                    <PlusCircleIcon className="w-4 h-4 mr-1.5" data-id="element-6262" />
                    Add New Line
                  </Button>
                  <div className="w-px h-6 bg-gray-300" data-id="element-6263"></div>
                </>}

              {isEditMode && !isEditAllMode ? <>
                  <button onClick={handleSaveAllChanges} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors" data-id="element-6264">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-6265" />
                    Save
                  </button>
                  <button onClick={handleCancelAllChanges} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-6266">
                    <XIcon className="w-4 h-4" data-id="element-6267" />
                    Cancel
                  </button>
                </> : !isEditMode ? <Button variant="ghost" size="sm" onClick={handleEditModeToggle} data-id="element-6268">
                  <Edit3Icon className="w-4 h-4 mr-1.5" data-id="element-6269" />
                  Edit
                </Button> : null}
            </div>}
        </div>

        {/* Table content */}
        {isExpanded && <div className="p-6" data-id="element-6270">
            <div className="border border-gray-200 rounded-lg" data-id="element-6271">
              <div className="overflow-x-auto" data-id="element-6272">
                <table className="w-full text-sm" data-id="element-6273">
                  <thead className="bg-gray-50" data-id="element-6274">
                    <tr data-id="element-6275">
                      {(matchingType === '2-way' || matchingType === '3-way') && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200 w-8" data-id="element-6276"></th>}
                      {!isNonPO && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6277">
                          Item Code
                        </th>}
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6278">
                        Description
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6279">
                        GL Code
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6280">
                        Tax Code
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6281">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6282">
                        Unit Price
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6283">
                        Subtotal
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6284">
                        Tax
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6285">
                        Total
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6286">
                        Type
                      </th>
                      {isEditMode && <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6287">
                          Actions
                        </th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white" data-id="element-6288">
                    {displayLineItems.map((item, index) => {
                  const isNewLine = isAddingNewLine && index === displayLineItems.length - 1;
                  const isCredit = item.type === 'CR';
                  const hasMatching = item.poMatch || item.grMatch;
                  const isLineExpanded = expandedLineItems.has(index);
                  const isFullyMatched = item.poMatch?.remainingQuantity === 0;
                  const isEditing = isEditMode && !isNewLine;
                  const displayItem = isEditing && editedLineItem ? editedLineItem : isNewLine && newLineItem ? newLineItem : item;
                  let rowBgClass = '';
                  if (isNewLine) {
                    rowBgClass = 'bg-green-50';
                  } else if (hasMatching && !isLineExpanded) {
                    rowBgClass = 'bg-blue-50';
                  }
                  const hoverClass = hasMatching ? 'hover:bg-blue-100' : 'hover:bg-gray-50';
                  return <Fragment key={isNewLine ? 'new-line' : index} data-id="element-6289">
                          <tr className={`border-b border-gray-100 ${rowBgClass} ${hoverClass} transition-colors`} data-id="element-6290">
                            {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-2 py-3 whitespace-nowrap align-top" data-id="element-6291">
                                {hasMatching && !isNewLine && <button onClick={() => toggleLineItemExpanded(index)} className="p-1 hover:bg-gray-200 rounded transition-all" title={isLineExpanded ? 'Hide matching details' : 'Show matching details'} data-id="element-6292">
                                    <ChevronRightIcon className={`w-4 h-4 text-gray-600 transition-transform ${isLineExpanded ? 'rotate-90' : ''}`} data-id="element-6293" />
                                  </button>}
                              </td>}

                            {!isNonPO && <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        minWidth: '100px'
                      }} data-id="element-6294">
                                {isEditing || isNewLine ? <input ref={index === 0 ? firstInputRef : null} type="text" value={displayItem.itemCode} onChange={e => isNewLine ? updateNewLineField('itemCode', e.target.value) : updateEditedField('itemCode', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Item code" data-id="element-6295" /> : <div className="flex items-center gap-2" data-id="element-6296">
                                    <span className="font-medium text-gray-900" data-id="element-6297">
                                      {item.itemCode}
                                    </span>
                                    {hasMatching && <span className="flex items-center gap-1 text-xs text-green-700" data-id="element-6298">
                                        {isFullyMatched ? <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-6299" /> : <AlertCircleIcon className="w-3.5 h-3.5 text-amber-600" data-id="element-6300" />}
                                      </span>}
                                  </div>}
                              </td>}

                            <td className="px-2 py-3 align-top" style={{
                        minWidth: '200px'
                      }} data-id="element-6301">
                              {isEditing || isNewLine ? <textarea ref={isNonPO && index === 0 ? firstInputRef as any : null} value={displayItem.description} onChange={e => isNewLine ? updateNewLineField('description', e.target.value) : updateEditedField('description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Description" rows={2} data-id="element-6302" /> : <>
                                  <div className="text-gray-900 font-medium" data-id="element-6303">
                                    {item.description}
                                  </div>
                                  {item.notes && <div className="text-xs text-gray-500 mt-1 flex items-start gap-1" data-id="element-6304">
                                      <span className="text-gray-400" data-id="element-6305">↪</span>
                                      <span data-id="element-6306">{item.notes}</span>
                                    </div>}
                                </>}
                            </td>

                            <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        minWidth: '120px',
                        maxWidth: '160px'
                      }} data-id="element-6307">
                              {isEditMode && !isNewLine ? <button onClick={() => handleGLCodeClick(index)} className="w-full text-left" data-id="element-6308">
                                  {item.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block hover:bg-green-100 transition-colors" data-id="element-6309">
                                      {item.glCode}
                                    </code> : <div className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-6310">
                                      <CodeIcon className="w-3.5 h-3.5" data-id="element-6311" />
                                      Add GL
                                    </div>}
                                </button> : isNewLine ? <button onClick={() => setSelectedLineItemIndex(-1)} className="w-full text-left" data-id="element-6312">
                                  {displayItem.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block hover:bg-green-100 transition-colors" data-id="element-6313">
                                      {displayItem.glCode}
                                    </code> : <div className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-6314">
                                      <CodeIcon className="w-3.5 h-3.5" data-id="element-6315" />
                                      Add GL
                                    </div>}
                                </button> : <>
                                  {item.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block" data-id="element-6316">
                                      {item.glCode}
                                    </code> : <span className="text-sm text-gray-400" data-id="element-6317">
                                      No GL
                                    </span>}
                                </>}
                            </td>

                            <td className="px-2 py-3 align-top" style={{
                        width: '80px'
                      }} data-id="element-6318">
                              {isEditMode && !isNewLine ? <TaxCodeSelector selectedCode={item.taxCode} onChange={taxCode => handleTaxCodeChangeForItem(index, taxCode)} hasError={hasTaxCodeError} errorMessage={taxCodeErrorMessage} data-id="element-6319" /> : isNewLine ? <select value={displayItem.taxCode} onChange={e => updateNewLineField('taxCode', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-6320">
                                  <option value="I" data-id="element-6321">I</option>
                                  <option value="E" data-id="element-6322">E</option>
                                  <option value="Z" data-id="element-6323">Z</option>
                                </select> : <span className="text-sm text-gray-900" data-id="element-6324">
                                  {item.taxCode || '-'}
                                </span>}
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-6325">
                              {isEditMode || isNewLine ? <input type="text" inputMode="numeric" value={displayItem.quantity} onChange={e => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? 0 : parseInt(value, 10);
                            if (isNewLine) {
                              updateNewLineField('quantity', numValue);
                            } else {
                              const updatedItem = {
                                ...item,
                                quantity: numValue
                              };
                              const subtotal = numValue * item.unitPrice;
                              let taxRate = 0.1;
                              if (item.taxCode === 'E') taxRate = 0;
                              if (item.taxCode === 'Z') taxRate = 0;
                              const taxAmt = subtotal * taxRate;
                              const total = subtotal + taxAmt;
                              updatedItem.subtotal = subtotal;
                              updatedItem.taxAmt = taxAmt;
                              updatedItem.total = total;
                              updateDraftLineItem(index, updatedItem);
                            }
                          }
                        }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" data-id="element-6326" /> : <span className="text-gray-900 font-medium" data-id="element-6327">
                                  {item.quantity}
                                </span>}
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6328">
                              {isEditing || isNewLine ? <input type="text" inputMode="decimal" value={displayItem.unitPrice} onChange={e => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            isNewLine ? updateNewLineField('unitPrice', value) : updateEditedField('unitPrice', value);
                          }
                        }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0.00" data-id="element-6329" /> : <span className="text-gray-900" data-id="element-6330">
                                  {formatAmount(item.unitPrice, document.currency)}
                                </span>}
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6331">
                              <span className="text-gray-900 font-medium" data-id="element-6332">
                                {formatAmount(displayItem.subtotal, document.currency)}
                              </span>
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-6333">
                              <span className="text-gray-600" data-id="element-6334">
                                {formatAmount(displayItem.taxAmt, document.currency)}
                              </span>
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6335">
                              <span className="text-gray-900 font-semibold" data-id="element-6336">
                                {formatAmount(displayItem.total, document.currency)}
                              </span>
                            </td>

                            <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6337">
                              {isEditing || isNewLine ? <select value={displayItem.type} onChange={e => isNewLine ? updateNewLineField('type', e.target.value as 'CR' | 'DR') : updateEditedField('type', e.target.value as 'CR' | 'DR')} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-6338">
                                  <option value="CR" data-id="element-6339">CR</option>
                                  <option value="DR" data-id="element-6340">DR</option>
                                </select> : <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${isCredit ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-id="element-6341">
                                  {isCredit ? 'CR' : 'DR'}
                                </span>}
                            </td>

                            {isEditMode && <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-6342">
                                {isNewLine ? <div className="flex items-center justify-center gap-1" data-id="element-6343">
                                    <button onClick={handleSaveNewLine} className="p-1 hover:bg-green-50 rounded transition-colors" title="Save new line" data-id="element-6344">
                                      <SaveIcon className="w-4 h-4 text-green-600" data-id="element-6345" />
                                    </button>
                                    <button onClick={handleCancelNewLine} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Cancel" data-id="element-6346">
                                      <XIcon className="w-4 h-4 text-gray-600" data-id="element-6347" />
                                    </button>
                                  </div> : deletingLineIndex === index ? <div className="flex items-center justify-center gap-1" data-id="element-6348">
                                    <button onClick={() => handleDeleteLine(index)} className="p-1 hover:bg-red-100 rounded transition-colors" title="Confirm delete" data-id="element-6349">
                                      <CheckCircle2Icon className="w-4 h-4 text-red-600" data-id="element-6350" />
                                    </button>
                                    <button onClick={handleCancelDelete} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Cancel delete" data-id="element-6351">
                                      <XIcon className="w-4 h-4 text-gray-600" data-id="element-6352" />
                                    </button>
                                  </div> : <div className="flex items-center justify-center gap-1" data-id="element-6353">
                                    {hasMatching && <button onClick={() => {
                            const updatedItem = {
                              ...item,
                              poMatch: undefined,
                              grMatch: undefined
                            };
                            updateDraftLineItem(index, updatedItem);
                          }} className="p-1 hover:bg-amber-50 rounded transition-colors" title="Clear PO/GR matching" data-id="element-6354">
                                        <UnlinkIcon className="w-4 h-4 text-amber-600" data-id="element-6355" />
                                      </button>}
                                    <button onClick={() => setDeletingLineIndex(index)} className="p-1 hover:bg-red-50 rounded transition-colors" title="Delete line item" data-id="element-6356">
                                      <TrashIcon className="w-4 h-4 text-red-500" data-id="element-6357" />
                                    </button>
                                  </div>}
                              </td>}
                          </tr>

                          {!isNewLine && isLineExpanded && item.poMatch && <tr className="bg-blue-50 border-b border-blue-100" data-id="element-6358">
                              {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-4 py-4" data-id="element-6359"></td>}
                              <td colSpan={isEditMode ? 11 : 10} className="px-4 py-4" data-id="element-6360">
                                <div className="flex items-start gap-6" data-id="element-6361">
                                  <div className="flex-1" data-id="element-6362">
                                    <div className="flex items-center justify-between mb-3" data-id="element-6363">
                                      <div className="flex items-center gap-2" data-id="element-6364">
                                        <div className="w-1 h-6 bg-blue-500 rounded" data-id="element-6365"></div>
                                        <h4 className="text-sm font-semibold text-blue-900" data-id="element-6366">
                                          Purchase Order Match
                                        </h4>
                                      </div>
                                      {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors" data-id="element-6367">
                                          <PencilIcon className="w-3.5 h-3.5" data-id="element-6368" />
                                          Edit PO Match
                                        </button>}
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-xs" data-id="element-6369">
                                      <div data-id="element-6370">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6371">
                                          PO Number
                                        </div>
                                        <code className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded border border-blue-200" data-id="element-6372">
                                          {item.poMatch.poNumber}
                                        </code>
                                      </div>
                                      <div data-id="element-6373">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6374">
                                          PO Line
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-6375">
                                          Line {item.poMatch.poLineNumber}
                                        </div>
                                      </div>
                                      <div data-id="element-6376">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6377">
                                          PO Description
                                        </div>
                                        <div className="text-gray-900" data-id="element-6378">
                                          {item.poMatch.poLineDescription}
                                        </div>
                                      </div>
                                      <div data-id="element-6379">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6380">
                                          PO Unit Price
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-6381">
                                          {formatAmount(item.poMatch.poUnitPrice, document.currency)}
                                        </div>
                                      </div>
                                      <div data-id="element-6382">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6383">
                                          PO Quantity
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-6384">
                                          {item.poMatch.poQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-6385">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6386">
                                          Matched Quantity
                                        </div>
                                        <div className="text-green-700 font-semibold" data-id="element-6387">
                                          {item.poMatch.matchedQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-6388">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6389">
                                          Remaining Quantity
                                        </div>
                                        <div className={`font-semibold ${isFullyMatched ? 'text-gray-400' : 'text-amber-700'}`} data-id="element-6390">
                                          {item.poMatch.remainingQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-6391">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6392">
                                          Match Status
                                        </div>
                                        <div className="flex items-center gap-1" data-id="element-6393">
                                          {isFullyMatched ? <>
                                              <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-6394" />
                                              <span className="text-green-700 font-medium" data-id="element-6395">
                                                Fully Matched
                                              </span>
                                            </> : <>
                                              <AlertCircleIcon className="w-4 h-4 text-amber-600" data-id="element-6396" />
                                              <span className="text-amber-700 font-medium" data-id="element-6397">
                                                Partial Match
                                              </span>
                                            </>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {item.grMatch && <div className="flex-1 border-l border-blue-200 pl-6" data-id="element-6398">
                                      <div className="flex items-center justify-between mb-3" data-id="element-6399">
                                        <div className="flex items-center gap-2" data-id="element-6400">
                                          <div className="w-1 h-6 bg-green-500 rounded" data-id="element-6401"></div>
                                          <h4 className="text-sm font-semibold text-green-900" data-id="element-6402">
                                            Goods Receipt
                                          </h4>
                                        </div>
                                        {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors" data-id="element-6403">
                                            <PencilIcon className="w-3.5 h-3.5" data-id="element-6404" />
                                            Edit GR Match
                                          </button>}
                                      </div>
                                      <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-6405">
                                        <div data-id="element-6406">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-6407">
                                            GR Number
                                          </div>
                                          <code className="text-sm font-mono bg-green-100 text-green-800 px-2 py-1 rounded border border-green-200" data-id="element-6408">
                                            {item.grMatch.grNumber}
                                          </code>
                                        </div>
                                        <div data-id="element-6409">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-6410">
                                            Receipt Date
                                          </div>
                                          <div className="text-gray-900 font-semibold" data-id="element-6411">
                                            {item.grMatch.grDate}
                                          </div>
                                        </div>
                                        <div data-id="element-6412">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-6413">
                                            Quantity Received
                                          </div>
                                          <div className="text-green-700 font-semibold" data-id="element-6414">
                                            {item.grMatch.grQuantity}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-3 flex items-center gap-1 text-xs text-green-700 font-medium" data-id="element-6415">
                                        <PackageIcon className="w-4 h-4" data-id="element-6416" />
                                        <span data-id="element-6417">
                                          Goods physically received and verified
                                        </span>
                                      </div>
                                    </div>}
                                </div>
                              </td>
                            </tr>}

                          {!isNewLine && !hasMatching && (matchingType === '2-way' || matchingType === '3-way') && isEditMode && <tr className="bg-amber-50 border-b border-amber-100" data-id="element-6418">
                                {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-4 py-3" data-id="element-6419"></td>}
                                <td colSpan={isEditMode ? 11 : 10} className="px-4 py-3" data-id="element-6420">
                                  <div className="flex items-center justify-between" data-id="element-6421">
                                    <div className="flex items-center gap-2 text-sm text-amber-800" data-id="element-6422">
                                      <AlertCircleIcon className="w-4 h-4" data-id="element-6423" />
                                      <span data-id="element-6424">
                                        This line is not matched to a Purchase
                                        Order
                                      </span>
                                    </div>
                                    <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-6425">
                                      <LinkIcon className="w-4 h-4" data-id="element-6426" />
                                      Match to PO
                                    </button>
                                  </div>
                                </td>
                              </tr>}
                        </Fragment>;
                })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-6427">
              <div className="flex items-center justify-between" data-id="element-6428">
                <div className="flex items-center gap-8 text-sm" data-id="element-6429">
                  <div className="flex items-baseline gap-2" data-id="element-6430">
                    <span className="text-gray-600" data-id="element-6431">Lines:</span>
                    <span className="font-semibold text-gray-900 text-base" data-id="element-6432">
                      {displayLineItems.length}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-6433">
                    <span className="text-gray-600" data-id="element-6434">Subtotal:</span>
                    <span className="font-semibold text-gray-900" data-id="element-6435">
                      {formatCurrency(document.subtotal, document.currency, true)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-6436">
                    <span className="text-gray-600" data-id="element-6437">Tax:</span>
                    <span className="font-semibold text-gray-900" data-id="element-6438">
                      {formatCurrency(document.taxAmt, document.currency, true)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-6439">
                    <span className="text-gray-600" data-id="element-6440">Total:</span>
                    <span className="font-bold text-lg text-gray-900" data-id="element-6441">
                      {formatCurrency(document.total, document.currency, true)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Modals */}
      {selectedLineItemIndex !== null && <GLCodingDialog lineItem={getGLLineItem(selectedLineItemIndex)} onApply={(glCode, selectedDimensions) => {
      if (selectedLineItemIndex === -1) {
        // New line item
        if (newLineItem) {
          setNewLineItem({
            ...newLineItem,
            glCode
          });
        }
      } else if (selectedLineItemIndex !== null) {
        // Existing line item
        const currentItem = isEditMode && draftLineItems.length > 0 ? draftLineItems[selectedLineItemIndex] : document.lineItems[selectedLineItemIndex];
        const updatedItem = {
          ...currentItem,
          glCode
        };
        // If in edit mode, update draft line items directly so the UI reflects the change
        if (isEditMode && draftLineItems.length > 0) {
          updateDraftLineItem(selectedLineItemIndex, updatedItem);
        }
        // Also call onUpdateLineItem to persist the change to the parent
        if (onUpdateLineItem) {
          onUpdateLineItem(selectedLineItemIndex, updatedItem);
        }
      }
      setSelectedLineItemIndex(null);
    }} onClose={handleGLCodeClose} data-id="element-6442" />}

      {showBulkGLModal && <BulkGLCodingModal lineItemCount={document.lineItems.length} vendor={document.supplier} onApply={handleBulkGLApply} onClose={() => setShowBulkGLModal(false)} data-id="element-6443" />}

      {showPreviousCodingModal && <PreviousCodingModal vendor={document.supplier} lineItemCount={document.lineItems.length} onApply={handlePreviousCodingApply} onClose={() => setShowPreviousCodingModal(false)} data-id="element-6444" />}

      {poMatchingLineIndex !== null && matchingType && matchingType !== 'non-po' && <POMatchingModal vendorName={document.supplier} invoiceTotal={document.total} invoiceCurrency={document.currency} matchingType={matchingType} onApply={(poNumber, selectedLines, matchingType) => {
      console.log('PO Matching applied:', {
        poNumber,
        selectedLines,
        matchingType
      });
      setPOMatchingLineIndex(null);
    }} onClose={handlePOMatchClose} data-id="element-6445" />}
    </>;
}